import React, { useEffect, useState } from "react";
import { Check } from "lucide-react";

// Self-playing DOM screencast of the Live Entry scoring flow. Shown next
// to the "How it works" section on the landing page. Illustrates the
// realistic workflow where photographers + titles are pre-populated and
// the operator just enters scores row-by-row after the judging night.
//
// No PNG, no video — just React state + CSS transitions. Loops
// indefinitely. Respects prefers-reduced-motion.

const BASE_ROWS = [
  { code: "492", name: "Sarah Bennett", title: "Kingfisher at Dawn", finalScore: 20, finalPlace: "1st" },
  { code: "104", name: "Charlotte Allen", title: "Rooftops in Fog", finalScore: 19, finalPlace: "2nd" },
  { code: "053", name: "Lily Davies", title: "Old Boot", finalScore: 18, finalPlace: "3rd" },
  { code: "892", name: "Tom Evans", title: "Estuary Light", finalScore: 17, finalPlace: "HC" },
  { code: "264", name: "Michael Wilson", title: "Steel Yard", finalScore: 16, finalPlace: "C" },
];

const STEP_MS = 1400; // dwell on each row
const RESET_MS = 2500; // pause at the end before looping

function useReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const m = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(m.matches);
    update();
    if (m.addEventListener) m.addEventListener("change", update);
    else m.addListener(update);
    return () => {
      if (m.removeEventListener) m.removeEventListener("change", update);
      else m.removeListener(update);
    };
  }, []);
  return reduced;
}

export default function LiveEntryScreencast() {
  const reduced = useReducedMotion();
  // Cursor index — which row the operator is currently scoring.
  // -1 means the whole grid is empty (start state); BASE_ROWS.length
  // means the whole grid is filled (end state).
  const [cursor, setCursor] = useState(reduced ? BASE_ROWS.length : -1);

  useEffect(() => {
    if (reduced) return;
    let cancelled = false;

    const tick = (i) => {
      if (cancelled) return;
      setCursor(i);
      if (i >= BASE_ROWS.length) {
        // Reached the bottom — pause a moment, then reset and loop.
        setTimeout(() => {
          if (!cancelled) tick(-1);
        }, RESET_MS);
        return;
      }
      setTimeout(() => tick(i + 1), STEP_MS);
    };

    // Kick off with a short delay so it's visible in the first paint.
    const start = setTimeout(() => tick(0), 700);
    return () => {
      cancelled = true;
      clearTimeout(start);
    };
  }, [reduced]);

  return (
    <div
      className="relative w-full"
      data-testid="landing-live-entry-screencast"
      aria-label="Animated preview of entering scores after a competition"
    >
      <div className="absolute -top-3 left-6 z-10 mono uppercase tracking-[0.25em] text-[10px] px-2.5 py-1 bg-amber-400 text-black rounded-sm shadow-lg">
        Entering scores
      </div>

      <div
        className="rounded-md p-4 pt-6"
        style={{
          background: "linear-gradient(180deg,#0d0d0d 0%, #050505 100%)",
          border: "1px solid #1f1f1f",
        }}
      >
        <div className="mono text-[9px] uppercase tracking-[0.28em] text-amber-400 mb-1">
          Live Entry
        </div>
        <div className="text-sm font-semibold tracking-tight leading-tight">
          Open PDI · Round 3
        </div>
        <div className="mono text-[9px] muted-text mb-3 mt-0.5">
          12 Feb 2026 · Judge: David Reeves ARPS
        </div>

        <div className="rounded-sm overflow-hidden" style={{ border: "1px solid #1a1a1a" }}>
          <div
            className="grid grid-cols-12 gap-1 px-2 py-1 mono text-[8px] uppercase tracking-wider muted-text"
            style={{ background: "#111" }}
          >
            <span className="col-span-1">#</span>
            <span className="col-span-1">I.D.</span>
            <span className="col-span-4">Photographer</span>
            <span className="col-span-3">Title</span>
            <span className="col-span-1 text-right">Sc.</span>
            <span className="col-span-2 text-right pr-1">Place</span>
          </div>
          {BASE_ROWS.map((r, i) => {
            const filled = cursor > i;
            const active = cursor === i;
            return (
              <div
                key={r.code}
                className="grid grid-cols-12 gap-1 px-2 py-1.5 text-[10px] items-center"
                style={{
                  borderTop: "1px solid #151515",
                  background: active
                    ? "rgba(251,191,36,0.06)"
                    : filled
                    ? "rgba(16,185,129,0.04)"
                    : "transparent",
                  transition: "background-color 400ms ease",
                }}
              >
                <span className="col-span-1 mono muted-text">
                  {i + 1}
                </span>
                <span className="col-span-1 mono">{r.code}</span>
                <span className="col-span-4 truncate">{r.name}</span>
                <span className="col-span-3 truncate muted-text">{r.title}</span>
                <span className="col-span-1 text-right mono">
                  <ScoreCell active={active} filled={filled} finalScore={r.finalScore} />
                </span>
                <span className="col-span-2 text-right pr-1">
                  {filled ? (
                    <PlacementBadge label={r.finalPlace} />
                  ) : (
                    <span className="muted-text mono text-[9px]">—</span>
                  )}
                </span>
              </div>
            );
          })}
        </div>

        <div className="flex items-center gap-1 mt-2 text-[9px] muted-text mono">
          <Check size={10} className="text-emerald-400" /> Enter jumps to the next score
        </div>
      </div>

      <p className="muted-text text-[11px] mt-2 mono uppercase tracking-[0.2em] text-right">
        Score after the night, or live as the judge talks — your call
      </p>
    </div>
  );
}

/* Score cell — shows empty, cursor bar (active), or the score (filled). */
function ScoreCell({ active, filled, finalScore }) {
  if (filled) {
    return <span className="mono font-semibold">{finalScore}</span>;
  }
  if (active) {
    // Blinking caret to signal "input is focused here".
    return (
      <span
        className="inline-block mono font-semibold text-amber-400"
        style={{
          borderBottom: "1.5px solid #fbbf24",
          minWidth: 12,
          animation: "ccs-caret-blink 900ms steps(2) infinite",
        }}
        aria-hidden="true"
      >
        |
      </span>
    );
  }
  return <span className="muted-text mono text-[9px]">—</span>;
}

function PlacementBadge({ label }) {
  const styles = {
    "1st": { bg: "rgba(251,191,36,0.18)", fg: "#fbbf24" },
    "2nd": { bg: "rgba(148,163,184,0.16)", fg: "#cbd5e1" },
    "3rd": { bg: "rgba(180,83,9,0.18)", fg: "#f59e0b" },
    HC: { bg: "rgba(16,185,129,0.12)", fg: "#10b981" },
    C: { bg: "rgba(59,130,246,0.12)", fg: "#60a5fa" },
  }[label] || { bg: "#111", fg: "#666" };
  return (
    <span
      className="inline-block px-1 rounded-sm mono text-[8px] font-semibold"
      style={{ background: styles.bg, color: styles.fg, border: `1px solid ${styles.fg}30` }}
    >
      {label}
    </span>
  );
}
